import React, { createContext, useContext, useEffect, useRef } from 'react'
import {
    CommonDialog,
    CommonDialogProps,
    CommonDialogRef,
} from '../components/CommonDialog'
import { Toast, ToastMessage } from 'primereact/toast'
import { ProgressDialogRef, ProgressDialog } from '../components/ProgressDialog'
import { usePopup } from '../hooks/popup'
import ETCINF0101 from 'app/pages/etc/inf/ETCINF0101'

/**
 * 공통 UI 닫기 핸들
 */
type hideHandle = {
    (): void
}
type DialogReturn = {
    /**
     * 다이얼로그를 표시한다.
     */
    show(props: CommonDialogProps): void
    /**
     * 다이얼로그를 닫는다.
     */
    hide: hideHandle
    /**
     * dialog 활성 여부
     */
    isOpen: boolean
}
type ToastReturn = {
    /**
     * 토스트를 표시한다.
     */
    show(props: ToastMessage): void
    /**
     * 토스트를 닫는다.
     */
    hide: hideHandle
}
type LoadingReturn = {
    /**
     * 로딩을 표시한다.
     */
    show(): void
    /**
     * 로딩을 닫는다.
     */
    hide: { (force?: boolean): void }
}
type CommonUIType = {
    /**
     * 다이얼로그를 사용한다.
     */
    useDialog(): DialogReturn
    /**
     * 토스트를 사용한다.
     */
    useToast(): ToastReturn
    /**
     * 로딩을 사용한다.
     */
    useProgressDialog(): LoadingReturn

    /**
     * 모바일이용안내팝업띄우기
     */
    openMobileGuide: () => void
}

export const CommonUIContext = createContext<CommonUIType>(undefined)

/**
 * 다이얼로그를 표시한다.
 * TODO: static class로 변경될 수 있음(할당되지 않았거나 엉뚱한걸 할당하면 런타임 오류)
 * @readonly
 */
export let commonDialog: (props: CommonDialogProps) => hideHandle = (_) => {
    return () => {}
}
/**
 * 토스트를 표시한다.
 * TODO: static class로 변경될 수 있음(할당되지 않았거나 엉뚱한걸 할당하면 런타임 오류)
 * @readonly
 */
export let commonToast: (props: ToastMessage) => hideHandle = (_) => {
    return () => {}
}
/**
 * 로딩을 표시한다.
 * TODO: static class로 변경될 수 있음(할당되지 않았거나 엉뚱한걸 할당하면 런타임 오류)
 * @readonly
 */
export let commonProgressDialog: () => hideHandle = () => {
    return () => {}
}

/**
 * 공통 UI Provider
 * @param param0
 * @returns
 */
export const CommonUIProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const dialogRef = useRef<CommonDialogRef>(null)
    const toastRef = useRef<Toast>(null)
    const progressRef = useRef<ProgressDialogRef>(null)
    const loadingRef = useRef<number>(0)

    //--dialog
    const showDialog = (props: CommonDialogProps) => {
        dialogRef.current?.show(props)
    }
    const hideDialog = () => {
        dialogRef.current?.hide()
    }
    const useDialog = () => {
        return {
            show: showDialog,
            hide: hideDialog,
            isOpen: dialogRef.current?.isOpen,
        }
    }
    //--toast
    const showToast = (props: ToastMessage) => {
        // Common override
        props.life = 2000
        props.closable = false
        toastRef.current?.show(props)
    }
    const hideToast = () => {
        toastRef.current?.clear()
    }
    const useToast = () => {
        return { show: showToast, hide: hideToast }
    }
    //--loading
    const showLoading = () => {
        loadingRef.current += 1
        progressRef.current?.show()
    }
    const hideLoading = (force?: boolean) => {
        if (force === true) {
            loadingRef.current = 0
            progressRef.current?.hide()
        } else {
            if (loadingRef.current - 1 >= 0) {
                loadingRef.current -= 1
            }

            if (loadingRef.current <= 0) {
                progressRef.current?.hide()
            }
        }
    }

    const useProgressDialog = () => {
        return { show: showLoading, hide: hideLoading }
    }

    useEffect(() => {
        commonDialog = (props: CommonDialogProps) => {
            showDialog(props)
            return hideDialog
        }
        commonToast = (props: ToastMessage) => {
            showToast(props)
            return hideToast
        }
        commonProgressDialog = () => {
            showLoading()
            return hideLoading
        }
    }, [])

    const mobilePopup = usePopup()

    const openMobileGuide = () => {
        mobilePopup.open()
    }

    return (
        <CommonUIContext.Provider
            value={{ useDialog, useToast, useProgressDialog, openMobileGuide }}
        >
            <CommonDialog ref={dialogRef} />
            <Toast ref={toastRef} position="bottom-center" />
            <ProgressDialog ref={progressRef} />
            <ETCINF0101
                visible={mobilePopup.state}
                onClose={mobilePopup.close}
            />
            {children}
        </CommonUIContext.Provider>
    )
}
/**
 * 공용 UI Hook을 호출한다. useDialog, useToast가 반환된다.
 * @returns
 */
export const useCommonUI = () => {
    const context = useContext(CommonUIContext)
    if (!context) throw new Error('Error: CommonUIContext context missing')
    return context
}
/**
 * 공용 Dialog Hook을 호출한다. show, hide가 반환된다.
 * @returns
 */
export const useDialog = () => {
    const context = useContext(CommonUIContext)
    if (!context) throw new Error('Error: CommonUIContext context missing')
    return context.useDialog()
}
/**
 * 공용 Toast Hook을 호출한다. show, hide가 반환된다.
 * @returns
 */
export const useToast = () => {
    const context = useContext(CommonUIContext)
    if (!context) throw new Error('Error: CommonUIContext context missing')
    return context.useToast()
}
/**
 * 공용 Progress Dialog Hook을 호출한다. show, hide가 반환된다.
 * @returns
 */
export const useProgressDialog = () => {
    const context = useContext(CommonUIContext)
    if (!context) throw new Error('Error: CommonUIContext context missing')
    return context.useProgressDialog()
}
